<template>
  <article class="card card-side bg-base-200 shadow-sm">
    <figure class="group relative shrink-0">
      <ScannerQrCode
        :data="companionUrl"
        scalable
        class="bg-base-300 flex w-36 items-center p-2 shadow-sm"
      />
      <button
        type="button"
        class="bg-base-200/60 absolute inset-0 flex-col items-center justify-center px-3 text-center backdrop-blur-xs group-hover:flex"
        :class="isRegenerating || !getPairingToken(session.id) ? 'flex' : 'hidden'"
        @click="onRegenerate"
      >
        <span
          class="btn btn-circle btn-sm"
          :class="{ 'btn-success': hasRegenerated }"
          :title="$t('dialog.companion.regenerate')"
        >
          <span v-if="isRegenerating" class="loading loading-spinner loading-xs" />
          <FontAwesomeIcon v-else :icon="hasRegenerated ? faCheck : faRotateLeft" />
        </span>
        <div class="absolute right-2 bottom-2 left-2 text-xs">
          {{ $t('dialog.companion.regenerate-hint') }}
        </div>
      </button>
    </figure>
    <div class="card-body relative gap-4">
      <div>
        <div class="float-right mb-2 text-right text-xs opacity-70">
          {{ $d(session.createdAt, 'shortDateTime') }}
        </div>
        <span
          v-for="role in session.roles"
          :key="role"
          class="badge badge-lg mr-2 mb-2 text-nowrap"
          :class="getCompanionRoleClass(role, 'badge')"
        >
          <FontAwesomeIcon :icon="getCompanionRoleIcon(role)" class="mr-1" />
          {{ getCompanionRoleTitle(role, 'title') }}
        </span>
        <a
          class="mb-2 block text-lg font-semibold"
          :href="companionUrl"
          target="_blank"
          rel="noopener noreferrer"
        >
          {{ session.name }}
        </a>
      </div>
      <p>
        {{ $t('dialog.companion.scan-me') }}
      </p>
      <div class="card-actions justify-end">
        <slot name="actions" />
      </div>
    </div>
  </article>
</template>

<script setup lang="ts">
import { faCheck, faRotateLeft } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import type { ResponseCompanionSessionDto } from '@konsolenkost-de/acquisition_store_server_sdk_vue';
import { computed, ref } from 'vue';
import { useCompanionRoleInfo } from '../../composables/companion/useCompanionRoleInfo';
import { useCompanionSessions } from '../../composables/companion/useCompanionSessions';
import ScannerQrCode from '../ScannerQrCode.vue';

const props = defineProps<{
  session: ResponseCompanionSessionDto;
}>();

const { getCompanionRoleIcon, getCompanionRoleClass, getCompanionRoleTitle } =
  useCompanionRoleInfo('badge');
const { regeneratePairingToken, getPairingToken } = useCompanionSessions();

const isRegenerating = ref(false);
const hasRegenerated = ref(false);
const resetHasRegeneratedTimeout = ref<number | null>(null);

const companionUrl = computed(() => {
  const url = new URL('pair', import.meta.env.VITE_COMPANION_URL || window.location.origin);
  url.searchParams.set('token', getPairingToken(props.session.id)?.pairingToken ?? '');
  return url.toString();
});

const onRegenerate = async () => {
  isRegenerating.value = true;
  await regeneratePairingToken(props.session.id);
  isRegenerating.value = false;
  hasRegenerated.value = true;

  if (resetHasRegeneratedTimeout.value) {
    clearTimeout(resetHasRegeneratedTimeout.value);
  }
  resetHasRegeneratedTimeout.value = window.setTimeout(() => {
    hasRegenerated.value = false;
  }, 5000);
};
</script>
